import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { SubSink } from "subsink";
import { MessagingService } from "../../core/messaging/messaging.service";
import { SeverityType } from "../../core/messaging/severity-type.enum";
import { FormService } from "../../dynamic-forms/form.service";
import { TextboxType } from "../../dynamic-forms/inputs/textbox/textbox-type.enum";
import { Textbox } from "../../dynamic-forms/inputs/textbox/textbox.model";
import { MemberItem } from "../../platform/api/member-v2/member-item.model";
import { MemberV2Service } from "../../platform/api/member-v2/member-v2.service";
import { MEMBER_MEMBER_QUERY_GRID } from "../../platform/modules/member/chase-detail/chase-detail-chart/attributes";
import { NumberHelper } from "../../utilities/contracts/number-helper";
import { ObjectHelper } from "../../utilities/contracts/object-helper";
import { StringHelper } from "../../utilities/contracts/string-helper";
import { RegExHelper } from "../../utilities/reg-Ex-Helper";
import { IFilter } from "../grid/basic-filter-applied/basic-filter-applied.component";
import { BasicGridComponent } from "../grid/basic-grid/basic-grid.component";
import { GridView } from "../grid/grid-menu/grid-views/grid-view.model";
import { GridViewsState } from "../grid/grid-menu/grid-views/grid-views-state.model";
import { GridColumnDefinition } from "../grid/models/grid-column-definition.model";
import { GridConfiguration } from "../grid/models/grid-configuration.model";

@Component({
  selector: "app-member-search",
  templateUrl: "./member-search.component.html",
  styleUrls: ["./member-search-component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberSearchComponent implements OnInit, OnDestroy {

  @Output() formClose = new EventEmitter();
  Filterform: FormGroup;
  readonly memberIdInput: Textbox;
  readonly memberFirstNameInput: Textbox;
  readonly memberLastNameInput: Textbox;
  readonly memberDateOfBirthInput: Textbox;

  gridConfigurationModel: GridConfiguration;
  @ViewChild(BasicGridComponent, { static: true }) basicGridComponent: BasicGridComponent;
  views: GridViewsState;
  refreshViews = new EventEmitter<GridView>(true);
  private sink = new SubSink();

  searchRequest: any;
  memberData: any[];
  selectedMember: MemberItem | null = null;
  request: IFilter[];
  isReloadFilterInput = false;
  memberDateOfBirth: Textbox;
  form: FormGroup;
  stateName = "member-query-new";
  constructor(
    private readonly formService: FormService,
    private readonly messagingService: MessagingService,
    private memberService: MemberV2Service,
    private changeDetector: ChangeDetectorRef
  ) {

    this.memberIdInput = new Textbox({
      key: "memberId",
      type: TextboxType.TEXT,
      label: "Member ID",
      validators: [Validators.maxLength(50)],
      errorMessages: {
        maxlength: `Invalid MemberID. Maximum 50 characters allowed`,
      },
    });

    this.memberFirstNameInput = new Textbox({
      key: "memberFirstName",
      label: "First Name",
      validators: [
        Validators.minLength(2),
        Validators.maxLength(100),
      ],
      errorMessages: {
        minlength: `Invalid Patient First Name.  Minimum 2 characters and Maximum 100 characters allowed`,
        maxlength: `Invalid Patient First Name.  Minimum 2 characters and Maximum 100 characters allowed`,
      },
    });

    this.memberLastNameInput = new Textbox({
      key: "memberLastName",
      label: "Last Name",
      validators: [
        Validators.minLength(2),
        Validators.maxLength(100),
      ],
      errorMessages: {
        minlength: `Invalid Patient Last Name.  Minimum 2 characters and Maximum 100 characters allowed`,
        maxlength: `Invalid Patient Last Name.  Minimum 2 characters and Maximum 100 characters allowed`,
      },
    });

    this.memberDateOfBirthInput = new Textbox({
      key: "memberDOB",
      label: "DOB",
      validators: [Validators.maxLength(10), Validators.pattern(RegExHelper.dateYearFirstPattern)],
      errorMessages: {
        maxlength: "DOB can not be more than 10 characters.",
        pattern: "DOB Must be in format YYYY-MM-DD",
      },
      placeholder: "YYYY-MM-DD",
    });


    this.form = this.formService.createFormGroup([
      this.memberIdInput,
      this.memberFirstNameInput,
      this.memberLastNameInput,
      this.memberDateOfBirthInput]
    );
  }

  ngOnInit() {
    this.createGrid();
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
  }

  private getColumns(): GridColumnDefinition[] {
    const defaultColumns = [
      new GridColumnDefinition({
        field: "m360MemberId",
        header: "Reveleer Member ID",
        routeUrl: "/chartlake/membersnew/:memberId/:memRecordId",
      }),
      new GridColumnDefinition({ field: "firstName", header: "First Name" }),
      new GridColumnDefinition({ field: "lastName", header: "Last Name" }),
      new GridColumnDefinition({ field: "dob", header: "DOB" }),
      new GridColumnDefinition({ field: "gender", header: "Gender" }),
      new GridColumnDefinition({
        field: "memberId",
        header: "Customer Member ID",
      }),
    ];

    return defaultColumns;
  }

  createGrid(): void {
    this.gridConfigurationModel = new GridConfiguration({
      columns: this.getColumns(),
      showExportAction: false,
      showActionColumn: false,
      viewAttributeId: MEMBER_MEMBER_QUERY_GRID.attributeId,
      stateName: this.stateName,
      showViews: true,
      selectionMode: "single",
    });
  }

  validateFilterForm(): boolean {
    if (!this.form) {
      return false;
    }
    const firstName = this.form.get("memberFirstName")?.value;
    const lastName = this.form.get("memberLastName")?.value;
    const memberId = this.form.get("memberId")?.value;

    return firstName || lastName || memberId ? true : false;
  }

  closeForm(selectedMember: MemberItem | null): void {
    this.formClose.emit(selectedMember);
  }

  search(): void {
    if (this.form.invalid) {
      return;
    }
    this.fetchMembers();
  }

  selectAndClose() {
      if (this.selectedMember) {
        const member = this.selectedMember as MemberItem;
        this.closeForm(member);
      } else {
        this.messagingService.showToast("Please select a Member first.", SeverityType.WARN);
      }
    }


  fetchMembers(token: string = ""): void {
    const payload = this.createMemberSearchPayload(token);

    this.memberService
      .searchMembers(payload, true)
      .subscribe(this.assignAndNotify);
  }

  private createMemberSearchPayload(token: string = ""): MemberItem {
    const payload: any = {
      paginationToken: token,
      pageSize: 100,
    };

    const form = this.form;

    if (StringHelper.isAvailable(form.get("memberId").value)) {
      payload.memberId = form.get("memberId").value;
    }
    if (StringHelper.isAvailable(form.get("memberFirstName").value)) {
      payload.firstName = form.get("memberFirstName").value;
    }
    if (StringHelper.isAvailable(form.get("memberLastName").value)) {
      payload.lastName = form.get("memberLastName").value;
    }
    if (StringHelper.isAvailable(form.get("memberDOB").value)) {
      payload.dob = form.get("memberDOB").value;
    }

    return payload;
  }


  clearControls(): void {
    this.form.reset();
    this.memberData = [];
    this.changeDetector.markForCheck();
  }

  private assignAndNotify = (data): void => {
    this.memberData = data.memberRecords as unknown as MemberItem[];
    this.changeDetector.markForCheck();
  }

}
